import { PropsWithChildren, ReactNode, useState } from "react";
import { OrganizationTalentIndicatorFragment } from "types/graphql-schema";

import AssessmentQuestionRangeOptions from "@apps/assessments/components/assessment-question-range-options";
import useLabel from "@apps/use-label/use-label";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import {
  ProgramFormCoreValueReviewConfig,
  ProgramFormGoalReviewConfig,
  ProgramFormRoleBasedReviewConfig,
} from "../types";
import { useCoreValues } from "./system-question-summaries";

// One question as reviewers will see it, shown in the settings for the
// questions Topicflow adds. It follows the scale being edited beside it.

type Scale = {
  assessmentScale: number;
  assessmentScaleStartsAt: number;
  assessmentScaleLabels: string[];
};

// A blue bar asking whether the admin wants to see an example, which opens
// the example under it.
const ExampleQuestionToggle = ({
  note,
  children,
}: PropsWithChildren<{
  // Says how the example stands for the rest of the questions.
  note: string;
}>) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex flex-col gap-3 rounded-md bg-blue-50 px-4 py-3">
      <button
        type="button"
        className="flex items-center justify-between gap-4 text-left text-sm font-medium text-blue-800"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flex items-center gap-2">
          <TopicflowIcon type="eye" className="h-4 w-4 shrink-0" />
          Want to see an example question?
        </span>
        <span className="flex shrink-0 items-center text-blue-600 hover:text-blue-800">
          {isOpen ? "Hide" : "Show"}
          <ToggleChevronIcon expanded={isOpen} />
        </span>
      </button>
      {isOpen && (
        <>
          <p className="text-xs text-blue-900">{note}</p>
          {children}
        </>
      )}
    </div>
  );
};

// Laid out as in the review: title, description, then the scale.
const ExampleQuestionCard = ({
  title,
  madeUp = false,
  description,
  startValue,
  size,
  labels,
  labelDescriptions = [],
}: {
  title: string;
  // Marks a question standing in for each person's own.
  madeUp?: boolean;
  description?: ReactNode;
  startValue: number;
  size: number;
  labels: ReadonlyArray<string | null>;
  labelDescriptions?: ReadonlyArray<string | null>;
}) => (
  <div className="flex flex-col gap-2 rounded-md border bg-white p-4">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
      {title}
      {madeUp && (
        <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500">
          Example
        </span>
      )}
    </div>
    {description && (
      <div className="whitespace-pre-line text-sm text-gray-500">
        {description}
      </div>
    )}
    <AssessmentQuestionRangeOptions
      disabled
      startValue={startValue}
      endValue={startValue + size - 1}
      labels={labels}
      labelDescriptions={labelDescriptions}
    />
  </div>
);

const scaleProps = (config: Scale) => ({
  startValue: config.assessmentScaleStartsAt,
  size: config.assessmentScale,
  labels: config.assessmentScaleLabels,
});

export const CoreValueExampleQuestion = ({
  config,
}: {
  config: ProgramFormCoreValueReviewConfig;
}) => {
  const coreValues = useCoreValues();
  const coreValue = coreValues[0];
  if (!coreValue) {
    return null;
  }
  return (
    <ExampleQuestionToggle
      note={`Reviewers rate each of your ${coreValues.length} core values this way, then give one overall Core Values rating.`}
    >
      <ExampleQuestionCard
        title={`${coreValue.emoji ? `${coreValue.emoji} ` : ""}${
          coreValue.title
        }`}
        description={coreValue.description}
        {...scaleProps(config)}
      />
    </ExampleQuestionToggle>
  );
};

export const GoalExampleQuestion = ({
  config,
}: {
  config: ProgramFormGoalReviewConfig;
}) => {
  const label = useLabel();
  return (
    <ExampleQuestionToggle
      note={`Reviewers rate each of the person's ${label("goal", {
        pluralize: true,
      })} from the ${label(
        "review",
      )} period this way, then give one overall rating. The ${label(
        "goal",
      )} below is made up.`}
    >
      <ExampleQuestionCard
        madeUp
        title="Launch the new onboarding flow"
        {...scaleProps(config)}
      />
    </ExampleQuestionToggle>
  );
};

export const RoleExampleQuestion = ({
  config,
}: {
  config: ProgramFormRoleBasedReviewConfig;
}) => {
  const label = useLabel();
  return (
    <ExampleQuestionToggle
      note={`Reviewers rate each ${label(
        "competency",
      )} of the person's role this way. The ${label(
        "competency",
      )} below is made up.`}
    >
      <ExampleQuestionCard
        madeUp
        title="Communication"
        description="Shares clear, timely updates and adapts the message to the audience."
        {...scaleProps(config)}
      />
    </ExampleQuestionToggle>
  );
};

export const TalentIndicatorExampleQuestion = ({
  title,
  description,
  talentIndicator,
}: {
  title: string;
  // The description set for this review, which can differ from the indicator's.
  description: string;
  talentIndicator: OrganizationTalentIndicatorFragment;
}) => (
  <ExampleQuestionToggle
    note={`Each indicator is its own question with its own scale. Here is how ${title} looks to reviewers.`}
  >
    <ExampleQuestionCard
      title={title}
      description={
        !isEmptyValue(description) && (
          <TextareaWysiwyg
            className="text-gray-500"
            value={description}
            fullWidth
          />
        )
      }
      startValue={talentIndicator.scaleStartsAt}
      size={talentIndicator.scale}
      labels={talentIndicator.labels}
      labelDescriptions={talentIndicator.labelDescriptions}
    />
  </ExampleQuestionToggle>
);
